import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Service } from 'types/rest/assetCatalogTypes';
import { BlueprintDraft } from '../../../store/blueprints/blueprintsTypes';
import { ApplicationState } from '../../../store/rootReducer';
import ReactSelector from '../../../components/selectors/ReactSelector';

interface Props {
  service: Service;
  onSave: (draft: BlueprintDraft, serviceId: number) => void;
  onCancel: () => void;
}

const BlueprintFromServiceModal = ({ service, onSave, onCancel }: Props) => {
  const { t } = useTranslation('blueprints');
  const allBlueprints = useSelector((s: ApplicationState) => s.blueprints.blueprints);

  const [name, setName] = useState(service.name);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const tagOptions = useMemo(() => {
    const seen = new Set<string>();
    allBlueprints.flatMap(bp => bp.tags).forEach(tag => seen.add(tag));
    return Array.from(seen).map(tag => ({ label: tag, value: tag, key: tag }));
  }, [allBlueprints]);

  const handleCreate = () => {
    onSave(
      {
        id: null,
        name: name.trim(),
        description: description.trim(),
        tags,
        visibility: 'PRIVATE',
        thumbnail: '',
        version: '',
        transactionIds: [] as number[],
      },
      service.id!,
    );
  };

  return (
    <Modal show onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>{t('fromService.title', { name: service.name })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="small font-weight-bold">{t('form.name')}</label>
        <input
          data-test="bpfs-name"
          className="form-control mb-3"
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <label className="small font-weight-bold">{t('form.description')}</label>
        <textarea
          className="form-control mb-3"
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <label className="small font-weight-bold">{t('form.tagsLabel')}</label>
        <div className="mb-1">
          <ReactSelector
            options={tagOptions}
            preSelected={tags.map(tag => ({ label: tag, value: tag, key: tag }))}
            onUpdateTags={(selected: Array<{ label: string }> | null) =>
              setTags((selected ?? []).map(item => item.label))
            }
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-link" onClick={onCancel}>
          {t('fromService.cancel')}
        </button>
        <button
          type="button"
          data-test="bpfs-create"
          className="btn btn-primary"
          disabled={name.trim().length === 0}
          onClick={handleCreate}
        >
          {t('fromService.create')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BlueprintFromServiceModal;
